import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../admin/lib/supabaseClient';

interface PublicDocumentViewerProps {
  type: 'Invoice' | 'Quote';
}

export default function PublicDocumentViewer({ type }: PublicDocumentViewerProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [docData, setDocData] = useState<any>(null);

  useEffect(() => {
    const fetchDoc = async () => {
      setLoading(true);
      setError(null);
      try {
        const table = type === 'Invoice' ? 'billing_invoices' : 'billing_quotes';
        const itemsTable = type === 'Invoice' ? 'billing_invoice_items' : 'billing_quote_items';
        const foreignKey = type === 'Invoice' ? 'invoice_id' : 'quote_id';

        // 1. Fetch document metadata
        const { data: doc, error: docError } = await supabase
          .from(table)
          .select('*')
          .eq('id', id)
          .single();

        if (docError) {
          if (docError.code === 'PGRST116') {
            throw new Error('Document not found. The link might be incorrect, or the document has been removed.');
          }
          throw docError;
        }
        if (!doc) throw new Error('Document not found');

        // 2. Fetch line items
        const { data: items, error: itemsError } = await supabase
          .from(itemsTable)
          .select('*')
          .eq(foreignKey, id);

        if (itemsError) throw itemsError;

        setDocData({ ...doc, items: items || [] });
      } catch (err: any) {
        console.error(err);
        setError(err.message || 'Failed to load document');
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchDoc();
  }, [id, type]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-400">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-mini-red mb-4"></div>
        <p className="text-lg font-semibold">Loading your {type.toLowerCase()}...</p>
      </div>
    );
  }

  if (error || !docData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200 p-6 text-center">
        <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/50 rounded-2xl p-8 max-w-md shadow-sm">
          <span className="text-4xl mb-4 block">⚠️</span>
          <h1 className="text-xl font-bold text-red-700 dark:text-red-400 mb-2">Failed to load document</h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">{error || 'The document link might be invalid or expired.'}</p>
          <button 
            onClick={() => navigate('/')} 
            className="px-5 py-2.5 bg-gray-200 hover:bg-gray-300 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-xl font-semibold text-sm transition-colors cursor-pointer"
          >
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  const docNumber = type === 'Invoice' ? docData.invoice_number : docData.quote_number;
  const dateLabel = type === 'Invoice' ? 'Due Date' : 'Expiry Date';
  const dateValue = type === 'Invoice' ? docData.due_date : docData.expiry_date;
  const items = docData.items || [];

  return (
    <div className="min-h-screen bg-gray-50 py-0 md:py-8 font-sans antialiased text-gray-900">
      <style>{`
        /* Action bar styling */
        .action-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background-color: #1f2937;
          color: #ffffff;
          padding: 12px 24px;
          border-bottom: 1px solid #374151;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          position: sticky;
          top: 0;
          z-index: 100;
        }
        .action-bar-title {
          font-size: 14px;
          font-weight: 600;
        }
        .action-bar-buttons {
          display: flex;
          gap: 12px;
        }
        .action-bar button {
          border: none;
          padding: 8px 16px;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          font-size: 13px;
          transition: all 0.2s;
        }
        .print-btn {
          background-color: #ef4444;
          color: white;
        }
        .print-btn:hover {
          background-color: #dc2626;
        }
        .close-btn {
          background-color: #374151;
          color: #d1d5db;
          border: 1px solid #4b5563 !important;
        }
        .close-btn:hover {
          background-color: #4b5563;
          color: white;
        }

        /* Invoice Container Layout */
        .invoice-body {
          padding: 20px;
          max-width: 850px;
          margin: 0 auto;
        }
        .invoice-container {
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          padding: 40px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
          position: relative;
          overflow: hidden;
        }
        .accent-bar {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 8px;
          background: linear-gradient(to right, #ef4444, #2563eb, #8b5cf6);
        }
        .header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-top: 15px;
          margin-bottom: 30px;
        }
        .brand-section {
          display: flex;
          flex-direction: column;
        }
        .brand-logo-row {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .brand-name {
          font-size: 28px;
          font-weight: 900;
          color: #111827;
          letter-spacing: -0.025em;
        }
        .badge {
          display: inline-block;
          padding: 2px 8px;
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          border-radius: 4px;
        }
        .badge-red {
          color: #dc2626;
          border: 1px solid #fca5a5;
          background-color: #fef2f2;
          letter-spacing: 0.1em;
        }
        .brand-slogan {
          font-size: 10px;
          font-weight: 700;
          color: #9ca3af;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin: 4px 0 0 0;
        }
        .company-details {
          font-size: 12px;
          color: #6b7280;
          margin-top: 15px;
          line-height: 1.5;
        }
        .company-details strong {
          color: #374151;
        }
        .company-details p {
          margin: 2px 0;
        }
        .doc-summary {
          text-align: right;
        }
        .doc-title {
          font-size: 32px;
          font-weight: 800;
          color: #1f2937;
          margin: 0 0 5px 0;
          text-transform: uppercase;
          letter-spacing: -0.025em;
        }
        .status-badge {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 9999px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          border: 1px solid;
        }
        .status-paid, .status-accepted, .status-invoiced {
          color: #15803d;
          background-color: #f0fdf4;
          border-color: #bbf7d0;
        }
        .status-unpaid, .status-sent {
          color: #1d4ed8;
          background-color: #eff6ff;
          border-color: #dbeafe;
        }
        .status-overdue {
          color: #b91c1c;
          background-color: #fef2f2;
          border-color: #fee2e2;
        }
        .status-draft {
          color: #4b5563;
          background-color: #f3f4f6;
          border-color: #e5e7eb;
        }
        .status-cancelled, .status-declined {
          color: #9ca3af;
          background-color: #f9fafb;
          border-color: #f3f4f6;
        }
        .doc-meta {
          margin-top: 15px;
          font-size: 12px;
          color: #6b7280;
          line-height: 1.6;
        }
        .doc-meta p {
          margin: 2px 0;
        }
        .doc-meta strong {
          color: #374151;
        }
        .divider {
          border: 0;
          border-top: 1px solid #f3f4f6;
          margin: 24px 0;
        }
        .details-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 30px;
          margin-bottom: 35px;
        }
        @media (max-width: 640px) {
          .details-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }
        }
        .billed-title {
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          color: #9ca3af;
          letter-spacing: 0.1em;
          margin-top: 0;
          margin-bottom: 8px;
        }
        .client-info {
          font-size: 14px;
          color: #4b5563;
          line-height: 1.5;
        }
        .client-info strong {
          color: #111827;
          font-size: 16px;
        }
        .client-info p {
          margin: 4px 0;
        }
        .client-address {
          margin-top: 8px !important;
          color: #6b7280;
          font-size: 13px;
          white-space: pre-line;
        }
        .banking-card {
          background-color: #f9fafb;
          border: 1px solid #f3f4f6;
          border-radius: 12px;
          padding: 20px;
        }
        .banking-title {
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          color: #4b5563;
          letter-spacing: 0.1em;
          margin-top: 0;
          margin-bottom: 12px;
        }
        .banking-details {
          font-size: 12px;
          color: #4b5563;
        }
        .banking-row {
          display: flex;
          justify-content: space-between;
          padding: 4px 0;
        }
        .banking-label {
          color: #9ca3af;
        }
        .banking-val {
          font-weight: 600;
          color: #1f2937;
        }
        .banking-val.mono {
          font-family: monospace;
        }
        .ref-row {
          border-top: 1px solid #e5e7eb;
          margin-top: 8px;
          padding-top: 8px;
          align-items: center;
        }
        .ref-badge {
          font-family: monospace;
          font-weight: 700;
          color: #b91c1c;
          background-color: #fef2f2;
          border: 1px solid #fee2e2;
          padding: 2px 6px;
          border-radius: 4px;
        }
        .items-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 30px;
        }
        .items-table th {
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          color: #9ca3af;
          letter-spacing: 0.1em;
          padding: 12px 0;
          border-bottom: 2px solid #e5e7eb;
        }
        .items-table th.text-left { text-align: left; }
        .items-table th.text-center { text-align: center; }
        .items-table th.text-right { text-align: right; }
        .item-row td {
          padding: 12px 0;
          font-size: 14px;
          color: #4b5563;
          border-bottom: 1px solid #f3f4f6;
        }
        .item-desc {
          font-weight: 500;
          color: #1f2937;
        }
        .item-qty {
          text-align: center;
          color: #6b7280;
        }
        .item-price {
          text-align: right;
          color: #6b7280;
        }
        .item-total {
          text-align: right;
          font-weight: 600;
          color: #111827;
        }
        .summary-section {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 30px;
          margin-top: 20px;
        }
        @media (max-width: 640px) {
          .summary-section {
            flex-direction: column-reverse;
            gap: 20px;
          }
          .terms-card, .totals-card {
            max-width: 100% !important;
            width: 100% !important;
          }
        }
        .terms-card {
          flex: 1;
          background-color: #fafafa;
          border: 1px solid #f3f4f6;
          border-radius: 12px;
          padding: 16px;
          max-width: 50%;
        }
        .terms-card h3 {
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          color: #9ca3af;
          letter-spacing: 0.1em;
          margin-top: 0;
          margin-bottom: 8px;
        }
        .terms-card p {
          font-size: 12px;
          color: #6b7280;
          margin: 0;
          line-height: 1.5;
          white-space: pre-line;
        }
        .totals-card {
          width: 250px;
          font-size: 14px;
          font-weight: 500;
        }
        .totals-row {
          display: flex;
          justify-content: space-between;
          padding: 6px 0;
          color: #6b7280;
        }
        .totals-row.total-due {
          border-top: 2px solid #e5e7eb;
          padding-top: 12px;
          margin-top: 6px;
          font-size: 18px;
          font-weight: 800;
          color: #111827;
        }
        .totals-row.total-due .total-val {
          color: #dc2626;
        }
        .bottom-branding {
          text-align: center;
          border-top: 1px solid #f3f4f6;
          padding-top: 30px;
          margin-top: 50px;
        }
        .bottom-branding p {
          font-size: 12px;
          font-weight: 500;
          color: #6b7280;
          margin: 0;
        }
        .bottom-branding .slogan {
          font-size: 10px;
          font-weight: 600;
          color: #9ca3af;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          margin-top: 4px;
        }

        /* Print Media Styles */
        @media print {
          .action-bar {
            display: none !important;
          }
          body {
            background-color: #ffffff !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          .invoice-body {
            padding: 0 !important;
            max-width: 100% !important;
          }
          .invoice-container {
            border: none !important;
            box-shadow: none !important;
            padding: 0 !important;
            margin: 0 !important;
            max-width: 100% !important;
            border-radius: 0 !important;
          }
        }
      `}</style>

      {/* Top sticky action control bar */}
      <div className="action-bar">
        <div className="action-bar-title">{type} Preview - {docNumber}</div>
        <div className="action-bar-buttons">
          <button className="print-btn" onClick={() => window.print()}>Print / Save PDF</button>
          <button className="close-btn" onClick={() => navigate('/')}>Go to Homepage</button>
        </div>
      </div>

      <div className="invoice-body">
        <div className="invoice-container">
          {/* Header Accent Line */}
          <div className="accent-bar"></div>

          {/* Header Section */}
          <div className="header">
            <div className="brand-section">
              <div className="brand-logo-row">
                <span className="brand-name">MCG</span>
                <span className="badge badge-red">Restoration</span>
              </div>
              <p className="brand-slogan">Classic Mini Specialists</p>
              
              <div className="company-details">
                <strong>Gerrie's Classic Minis</strong>
                <p>Pretoria, South Africa</p>
                <p>Phone: 072 998 1365</p>
                <p>Email: mike@silvertoncc.co.za</p>
              </div>
            </div>
            
            <div className="doc-summary">
              <h1 className="doc-title">{type}</h1>
              <div>
                <span className={`status-badge status-${docData.status}`}>
                  {docData.status}
                </span>
              </div>
              
              <div className="doc-meta">
                <p><strong>Document No:</strong> <strong>{docNumber}</strong></p>
                <p><strong>Date Issued:</strong> {docData.issue_date}</p>
                <p><strong>{dateLabel}:</strong> <strong>{dateValue}</strong></p>
              </div>
            </div>
          </div>

          <hr className="divider" />

          {/* Client & Payment Details Grid */}
          <div className="details-grid">
            <div>
              <h2 className="billed-title">Billed To</h2>
              <div className="client-info">
                <strong>{docData.customer_name}</strong>
                {docData.customer_email ? <p>{docData.customer_email}</p> : ''}
                {docData.customer_phone ? <p>{docData.customer_phone}</p> : ''}
                {docData.customer_address ? <p className="client-address">{docData.customer_address}</p> : ''}
              </div>
            </div>
            
            <div className="banking-card">
              <h2 className="banking-title">Banking Details (EFT Payments)</h2>
              <div className="banking-details">
                <div className="banking-row">
                  <span className="banking-label">Bank Name:</span>
                  <span className="banking-val">Absa Bank</span>
                </div>
                <div className="banking-row">
                  <span className="banking-label">Account Name:</span>
                  <span className="banking-val">GJV EVERTS</span>
                </div>
                <div className="banking-row">
                  <span className="banking-label">Account Number:</span>
                  <span className="banking-val mono">1004543595</span>
                </div>
                <div className="banking-row">
                  <span className="banking-label">Branch Code:</span>
                  <span className="banking-val mono">632005</span>
                </div>
                <div className="banking-row ref-row">
                  <span className="banking-label">Reference:</span>
                  <span className="ref-badge">{docNumber}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Items Table */}
          <table className="items-table">
            <thead>
              <tr>
                <th className="text-left">Description</th>
                <th className="text-center">Qty</th>
                <th className="text-right">Unit Price</th>
                <th className="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item: any, idx: number) => {
                const priceZar = item.unit_price / 100;
                const totalZar = item.total_price / 100;
                const rate = parseFloat(docData.exchange_rate || 22);
                return (
                  <tr key={item.id || idx} className="item-row" style={{ verticalAlign: 'top' }}>
                    <td className="item-desc">{item.description}</td>
                    <td className="item-qty">{item.quantity}</td>
                    <td className="item-price">
                      <div>R {priceZar.toFixed(2)}</div>
                      {docData.is_gbp && (
                        <div style={{ fontSize: '10.5px', color: '#6b7280', fontWeight: 500 }}>
                          £ {(priceZar / rate).toFixed(2)}
                        </div>
                      )}
                    </td>
                    <td className="item-total">
                      <div>R {totalZar.toFixed(2)}</div>
                      {docData.is_gbp && (
                        <div style={{ fontSize: '10.5px', color: '#6b7280', fontWeight: 500 }}>
                          £ {(totalZar / rate).toFixed(2)}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Summary and Notes */}
          <div className="summary-section">
            <div className="terms-card">
              {docData.notes ? (
                <>
                  <h3>Terms & Conditions</h3>
                  <p>{docData.notes}</p>
                </>
              ) : ''}
            </div>

            <div className="totals-card">
              <div className="totals-row">
                <span>Subtotal:</span>
                <span>R {(docData.subtotal / 100).toFixed(2)}</span>
              </div>
              {docData.is_gbp && (
                <div className="totals-row" style={{ fontSize: '11px', color: '#6b7280', marginTop: '-6px' }}>
                  <span>Subtotal (GBP):</span>
                  <span>£ {((docData.subtotal / 100) / parseFloat(docData.exchange_rate || 22)).toFixed(2)}</span>
                </div>
              )}
              {docData.tax_amount && docData.tax_amount > 0 ? (
                <div className="totals-row">
                  <span>VAT ({docData.tax_rate || 15}%):</span>
                  <span>R {(docData.tax_amount / 100).toFixed(2)}</span>
                </div>
              ) : ''}
              <div className="totals-row total-due">
                <span>Total Due:</span>
                <span className="total-val">R {(docData.total_amount / 100).toFixed(2)}</span>
              </div>
              {docData.is_gbp && (
                <>
                  <div className="totals-row total-due-gbp" style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', fontWeight: '700', color: '#4b5563', marginTop: '2px' }}>
                    <span>Total Due (GBP):</span>
                    <span>£ {((docData.total_amount / 100) / parseFloat(docData.exchange_rate || 22)).toFixed(2)}</span>
                  </div>
                  <div className="totals-row exchange-rate-info" style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#9ca3af', marginTop: '8px', fontStyle: 'italic' }}>
                    <span>Exchange Rate:</span>
                    <span>1 GBP = R {parseFloat(docData.exchange_rate || 22).toFixed(2)}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Bottom Branding */}
          <div className="bottom-branding">
            <p>Thank you for your business! 🚗✨</p>
            <p className="slogan">Keeping Classic Minis Alive</p>
          </div>
        </div>
      </div>
    </div>
  );
}
